import * as React from 'react';

import { Form as RFFForm, FormSpy } from 'react-final-form';
import { AnyObject, FormApi, FormState } from 'final-form';
import { FormErrors } from 'src/util/FormErrors';
import AnimateHeight from 'react-animate-height';
import { isArray } from 'util';
import arrayMutators from 'final-form-arrays';

// import * as ReactDOM from 'react-dom';
import FormDebugPanel from './FormDebugPanel';

interface IFormProps {
    children: Array<JSX.Element> | JSX.Element;
    globalId?: string;
    validate?: (values: AnyObject) => FormErrors<AnyObject>;
    onSubmit: (values: AnyObject, form: FormApi) => void;
    onChange?: (state: FormState<AnyObject>, form: FormApi) => void;
    initialValues?: AnyObject;
    error?: string;
    showDebugValues?: boolean;
    hasFieldArray?: boolean;
    onFormCreated?: (form: FormApi) => void;
    style?: React.CSSProperties;
    key?: string;
    keepDirty?: boolean;
}

const Form = (props: IFormProps) => {
    const formPassedRef = React.useRef(false);

    const updateChild = (element: JSX.Element, index: number, form: FormApi, submitFailed: boolean, invalid: boolean) => {
        if (!element || !element.type || !(element.type as AnyObject).displayName) {
            return React.cloneElement(element, { key: `form-element-${index}` });
        }

        if (isArray(element) && element.length <= 0) {
            return element;
        }

        if ((element.type as AnyObject).displayName === 'Button' && element.props.type === 'submit') {
            return React.cloneElement(element, { ...element.props, disabled: submitFailed && invalid, key: `form-elem-${index}` });
        } else if ((element.type as AnyObject).displayName === 'FormTogglePanel') {
            let show = form.getState().values[element.props.toggleProperty.replace('!', '')];
            if (element.props.toggleProperty.startsWith('!')) {
                show = !show;
            }
            return React.cloneElement(element, { ...element.props, key: `form-toggle-panel-${index}`, showPanel: show });
        } else if ((element.type as AnyObject).displayName === 'FormSelect') {
            return React.cloneElement(element, { ...element.props, key: `form-select-wrapper-${index}`, form: form });
        } else {
            return React.cloneElement(element, { key: `form-element-${index}` });
        }
    };

    const recurseChildTree = (children: Array<JSX.Element>, form: FormApi, submitFailed: boolean, invalid: boolean): Array<JSX.Element> => {
        if (!children) {
            return [];
        }

        const newChildren = Array<JSX.Element>();

        if (isArray(children)) {
            children.map((element: JSX.Element, index: number) => {
                const e = updateChild(element, index, form, submitFailed, invalid);
                if (e && e.props && e.props.children) {
                    newChildren.push(React.cloneElement(e, { children: recurseChildTree(e.props.children, form, submitFailed, invalid) }));
                } else {
                    newChildren.push(e);
                }
            });
        } else {
            newChildren.push(children);
        }

        return newChildren;
    };

    return (
        <RFFForm
            key={props.key ? props.key : 'form'}
            keepDirtyOnReinitialize={props.keepDirty !== undefined ? props.keepDirty : false}
            onSubmit={props.onSubmit}
            validate={props.validate}
            initialValues={props.initialValues}
            mutators={props.hasFieldArray ? { ...arrayMutators } : undefined}
            render={({ handleSubmit, invalid, submitting, submitFailed, values, form }) => {
                if (props.globalId) {
                    window[props.globalId] = form;
                }

                if (!formPassedRef.current && props.onFormCreated) {
                    props.onFormCreated(form);
                    formPassedRef.current = true;
                }

                const newChildren = recurseChildTree(isArray(props.children) ? props.children : [props.children], form, submitFailed, invalid);

                const errorHeight = props.error && !submitting ? 'auto' : 0;

                return (
                    <>
                        {props.onChange && (
                            <FormSpy onChange={(state: FormState<AnyObject>) => props.onChange(state, form)} subscription={{ values: true }} />
                        )}
                        <form className="dlc-form" onSubmit={handleSubmit} style={props.style} id={props.globalId || undefined}>
                            <AnimateHeight height={errorHeight} duration={300}>
                                <div className="dlc-form-error">{props.error}</div>
                            </AnimateHeight>
                            {newChildren}
                        </form>
                        {props.showDebugValues && <FormDebugPanel formValues={values} />}
                    </>
                );
            }}
        />
    );
};

export default Form;



// WEBPACK FOOTER //
// ./src/components/controls/forms/Form.tsx